// @flow
import { metrics } from '@seek/metrics-js';
import { logger } from 'src/modules/logger';
import instrumentMethods from 'instrument-methods';
import { get, isEmpty } from 'lodash';
import apis from 'src/modules/seek-jobs-api-client';
import { parse as parseCookies } from 'cookie';

import {
  GET_RESULTS_SUCCESS,
  RESULTS_PAGE_LOAD
} from 'src/store/results/results';
import { GET_ACCOUNT_SUCCESS, UPDATE_AUTHENTICATED } from 'src/store/user/user';
import { SORT_MODE_CHANGED } from 'src/store/search/search';
import {
  LOCALE_CHANGED,
  LOCATION_CHANGED,
  TRACKED_LINK_CLICKED
} from 'src/store/location/location';
import { HOME_PAGE_LOADED } from 'src/store/home/home';
import {
  SAVE_JOB_SUCCESS,
  REMOVE_SAVE_JOB_SUCCESS,
  SIGNED_OUT_SAVE_JOB
} from 'src/store/savedJobs/savedJobs';
import {
  SAVE_SEARCH_SUCCESS,
  JOB_MAIL_SUCCESS
} from 'src/store/saveSearch/saveSearch';
import { LMIS_IMPRESSION } from 'src/store/lmis/lmis';
import {
  JOB_EXPIRED,
  JOB_DETAILS_FETCH_FAILURE,
  JOB_DETAILS_FETCH_SUCCESS,
  JOB_DETAILS_PAGE_LOADED,
  CIS_INTERACTION_EVENT,
  SHARE_LINK_CLICKED,
  SEND_JOB_SUCCESS
} from 'src/store/jobdetails/jobdetails';
import { formatExperiments } from 'src/modules/experiments-helpers';
import { APP_BANNER_IMPRESSION } from 'src/store/appInstallBanner/appInstallBanner';
import {
  ANSWER_NUDGE,
  DISMISS_NUDGE,
  NUDGE_IMPRESSION
} from 'src/store/nudges/nudges';

const EXPERIMENTS_INITIALISED = '__EXPERIMENTS_INITIALISED';

const analyticsLogger = (methodName, args) => {
  if (typeof window !== 'undefined' && window.ANALYTICS_DEBUG) {
    // eslint-disable-next-line no-console
    console.log(`analytics.${methodName}()`, ...args);
  }

  metrics.count('analytics', [`analytics-name:${methodName}`]);
};

const instrumentAnalytics = analytics => {
  return instrumentMethods(analytics, { after: analyticsLogger });
};

const experimentsReducer = (accum, { name, variation }) => [
  ...accum,
  { id: name, variant: variation.index }
];

export default ({ analytics }: { analytics: Analytics }) =>
  instrumentAnalytics(analytics) &&
  (store => next => action => {
    if (typeof action === 'function') {
      return action(store.dispatch, store.getState);
    }

    const { type, payload, meta } = action;

    const {
      appBannerImpression,
      shareLinkClicked,
      experiments: registerExperiments,
      searchPerformed,
      userDetailsUpdated,
      locationChanged,
      localeChanged,
      searchResultsPageLoaded,
      sortModeChanged,
      homePageLoaded,
      createSaveSearchClicked,
      createJobMailClicked,
      saveJobClicked,
      savedJobCreated,
      savedJobRemoved,
      linkClicked,
      lmisImpression,
      // job details
      jobDetailsFetched,
      jobDetailsPageLoaded,
      contactMatchClicked,
      // send job
      sendJobSuccessLoaded,
      sendJobFormLoaded
    } = analytics;

    try {
      switch (type) {
        case APP_BANNER_IMPRESSION: {
          appBannerImpression(payload);
          break;
        }

        case EXPERIMENTS_INITIALISED: {
          const formattedExperiments = formatExperiments(
            experimentsReducer,
            payload
          );
          registerExperiments(formattedExperiments);
          break;
        }

        case GET_RESULTS_SUCCESS: {
          const { query, result, userQueryId } = payload;

          searchPerformed({ query, result, userQueryId });
          break;
        }

        case UPDATE_AUTHENTICATED: {
          const { authenticated } = payload;

          userDetailsUpdated({ authenticated });
          break;
        }

        case GET_ACCOUNT_SUCCESS: {
          const { emailCorrelationId } = payload;

          userDetailsUpdated({ emailCorrelationId });
          break;
        }

        case RESULTS_PAGE_LOAD: {
          searchResultsPageLoaded();
          break;
        }

        case LOCALE_CHANGED: {
          localeChanged(payload.locale);
          break;
        }

        case LOCATION_CHANGED: {
          locationChanged(payload.location);
          break;
        }

        case SORT_MODE_CHANGED: {
          sortModeChanged();
          break;
        }

        case HOME_PAGE_LOADED: {
          const { surveyName } = meta;
          homePageLoaded({ surveyName });
          break;
        }

        case SAVE_SEARCH_SUCCESS: {
          const saveSearchPosition = get(payload, 'saveSearchPosition');
          const analyticsPayload = saveSearchPosition
            ? { saveSearchPosition }
            : undefined; // eslint-disable-line
          createSaveSearchClicked(analyticsPayload);
          break;
        }

        case JOB_MAIL_SUCCESS: {
          createJobMailClicked();
          break;
        }

        case TRACKED_LINK_CLICKED: {
          const { analytics: linkName, href } = payload;

          linkClicked({ href, linkName });
          break;
        }

        case SIGNED_OUT_SAVE_JOB: {
          saveJobClicked();
          break;
        }

        case SAVE_JOB_SUCCESS: {
          savedJobCreated();
          break;
        }

        case REMOVE_SAVE_JOB_SUCCESS: {
          savedJobRemoved();
          break;
        }

        case LMIS_IMPRESSION: {
          const { lmisType, lmisContextType } = payload;
          lmisImpression({ lmisType, lmisContextType });
          break;
        }

        case JOB_EXPIRED:
        case JOB_DETAILS_FETCH_SUCCESS:
        case JOB_DETAILS_FETCH_FAILURE: {
          const { result: job } = payload;

          jobDetailsFetched(job);

          break;
        }

        case JOB_DETAILS_PAGE_LOADED: {
          const {
            job: { id, desktopAdTemplate, queryString: jobQueryString },
            query: {
              send: isSendJobPage,
              userqueryid,
              tracking,
              type: queryType
            },
            seekerId,
            jobDetailsViewedCorrelationId
          } = payload;

          const {
            jobdetails,
            fitme: { showTopApplicantBadge },
            location: { locale }
          } = store.getState();
          const branding = get(jobdetails, 'result.branding');
          const coverImage = get(branding, 'cover.url');

          const brandingFlags = {
            isBranded: !isEmpty(branding),
            hasCoverImage: !isEmpty(coverImage),
            hasCustomTemplate: !isEmpty(desktopAdTemplate)
          };

          if (isSendJobPage) {
            sendJobFormLoaded();
          } else {
            jobDetailsPageLoaded({
              brandingFlags,
              showTopApplicantBadge
            });

            if (ENV.CLIENT) {
              const eventCapturePayload = {
                locale,
                eventName: 'legacy-job-details-viewed',
                event: {
                  jobId: id,
                  adDisplayType: get(jobQueryString, 'type', queryType),
                  userQueryId: userqueryid,
                  omnitureTracking: tracking || '',
                  candidateId: seekerId,
                  jobDetailsViewedCorrelationId,
                  JobseekerSessionId: parseCookies(document.cookie)
                    .JobseekerSessionId
                }
              };

              apis().eventCapture.putToEventCapture(eventCapturePayload);
            }
          }

          break;
        }

        case SEND_JOB_SUCCESS: {
          sendJobSuccessLoaded();

          break;
        }

        case CIS_INTERACTION_EVENT: {
          const { type: contactType, eventType: interactionType } = payload;
          contactMatchClicked({ interactionType, contactType });
          break;
        }

        case SHARE_LINK_CLICKED: {
          shareLinkClicked(payload);
          break;
        }

        case ANSWER_NUDGE:
        case DISMISS_NUDGE:
        case NUDGE_IMPRESSION: {
          const { href } = store.getState().location;
          const linkName = {
            NUDGE_IMPRESSION: 'nudge-impression',
            ANSWER_NUDGE: 'nudge-answer',
            DISMISS_NUDGE: 'nudge-dismiss'
          }[type];
          linkClicked({ href, linkName });
          break;
        }

        default: {
          break;
        }
      }
    } catch (err) {
      logger.error({ err }, 'Error occurred in analytics middleware');
    }
    return next(action);
  });
